import { useState } from 'react';
import { ChromePicker } from 'react-color';
import styles from './FlowchartToolbar.module.css';

interface FlowchartNodeToolbarProps {
  node: any;
  onUpdate: (updates: any) => void;
  onClose: () => void;
}

export default function FlowchartNodeToolbar({ node, onUpdate, onClose }: FlowchartNodeToolbarProps) {
  const [showBgPicker, setShowBgPicker] = useState(false);
  const [showBorderPicker, setShowBorderPicker] = useState(false);

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate({
      data: {
        ...node.data,
        label: e.target.value
      }
    });
  };

  const handleBgColorChange = (color: any) => {
    const newColor = color.hex;
    onUpdate({
      data: {
        ...node.data,
        style: {
          ...node.data.style,
          backgroundColor: newColor
        }
      }
    });
  };

  const handleBorderColorChange = (color: any) => {
    const newColor = color.hex;
    onUpdate({
      data: {
        ...node.data,
        style: {
          ...node.data.style,
          borderColor: newColor
        }
      }
    });
  };

  const handleBorderWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newWidth = parseInt(e.target.value, 10);
    onUpdate({
      data: {
        ...node.data,
        style: {
          ...node.data.style,
          borderWidth: newWidth
        }
      }
    });
  };

  const handleNodeTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate({
      type: e.target.value
    });
  };

  return (
    <div className={styles.toolbarFloating}>
      <div className={styles.toolbarHeader}>
        <h3 className={styles.toolbarTitle}>Node Properties</h3>
        <button
          onClick={onClose}
          className={styles.toolbarClose}
        >
          ×
        </button>
      </div>

      <div className={styles.toolbarGroup}>
        <label className={styles.toolbarLabel}>Shape</label>
        <select
          value={node.data.shape || node.type || 'rectangle'}
          onChange={(e) => {
            handleNodeTypeChange(e);
            onUpdate({
              type: e.target.value,
              data: {
                ...node.data,
                shape: e.target.value
              }
            });
          }}
          className={styles.toolbarSelect}
        >
          <option value="rectangle">Rectangle (Process)</option>
          <option value="circle">Circle (Start/End)</option>
          <option value="ellipse">Ellipse (Process)</option>
          <option value="diamond">Diamond (Decision)</option>
          <option value="parallelogram">Parallelogram (Input/Output)</option>
        </select>
      </div>

      <div className={styles.toolbarGroup}>
        <label className={styles.toolbarLabel}>Connection Points</label>
        <select
          value={node.data.connections || 'all'}
          onChange={(e) => {
            onUpdate({
              data: {
                ...node.data,
                connections: e.target.value
              }
            });
          }}
          className={styles.toolbarSelect}
        >
          <option value="all">All Sides (Top, Bottom, Left, Right)</option>
          <option value="top">Top Only</option>
          <option value="bottom">Bottom Only</option>
          <option value="left">Left Only</option>
          <option value="right">Right Only</option>
          <option value="top-bottom">Top & Bottom</option>
          <option value="left-right">Left & Right</option>
        </select>
      </div>

      <div className={styles.toolbarGroup}>
        <label className={styles.toolbarLabel}>Label</label>
        <input
          type="text"
          value={node.data.label}
          onChange={handleLabelChange}
          className={styles.toolbarInput}
        />
      </div>

      <div className={styles.toolbarGroup}>
        <label className={styles.toolbarLabel}>Background Color</label>
        <div className={styles.colorPickerWrapper}>
          <button
            onClick={() => setShowBgPicker(!showBgPicker)}
            className={styles.colorButton}
            style={{ backgroundColor: node.data.style?.backgroundColor || '#ffffff' }}
          >
            <div
              className={styles.colorSwatch}
              style={{ backgroundColor: node.data.style?.backgroundColor || '#ffffff' }}
            />
            {node.data.style?.backgroundColor || '#ffffff'}
          </button>
          {showBgPicker && (
            <div className={styles.colorPickerPopup}>
              <ChromePicker
                color={node.data.style?.backgroundColor || '#ffffff'}
                onChange={handleBgColorChange}
              />
            </div>
          )}
        </div>
      </div>

      <div className={styles.toolbarGroup}>
        <label className={styles.toolbarLabel}>Border Color</label>
        <div className={styles.colorPickerWrapper}>
          <button
            onClick={() => setShowBorderPicker(!showBorderPicker)}
            className={styles.colorButton}
          >
            <div
              className={styles.colorSwatch}
              style={{ backgroundColor: node.data.style?.borderColor || '#000000' }}
            />
            {node.data.style?.borderColor || '#000000'}
          </button>
          {showBorderPicker && (
            <div className={styles.colorPickerPopup}>
              <ChromePicker
                color={node.data.style?.borderColor || '#000000'}
                onChange={handleBorderColorChange}
              />
            </div>
          )}
        </div>
      </div>

      <div className={styles.toolbarGroup}>
        <label className={styles.toolbarLabel}>
          Border Width: {node.data.style?.borderWidth || 2}px
        </label>
        <input
          type="range"
          min="0"
          max="10"
          value={node.data.style?.borderWidth || 2}
          onChange={handleBorderWidthChange}
          className={styles.toolbarRange}
        />
      </div>
    </div>
  );
}
